/**
 * useRoleDetailData — shared hook for fetching task, hierarchy, and
 * predecessor data when a role detail modal opens.
 *
 * Used by: EngagementsPage, ActiveRolesPanel, ProjectTeamPanel.
 * Avoids triplicating the fetch-and-join logic.
 *
 * DEV-631: Role-Based Gantt Task Assignment
 */

import { useState, useEffect, useMemo } from 'react';
import {
  fetchTaskRoleAssignmentsByRole,
  fetchTaskRoleAssignments,
  buildTaskRoleAssignmentMap,
  type EngagementTask,
  type TaskRoleAssignmentMap,
} from '../../api/ganttTaskRoleAssignments';
import { fetchGanttTasksByProject, type GanttTaskResponse } from '../../api/ganttTasks';
import { fetchProjectRoleSlots, type ProjectRoleSlotResponse } from '../../api/projectRoles';

/* ── Types ──────────────────────────────────────────────── */

export interface RoleDetailHierarchy {
  reportsTo: { roleLabel: string; assigneeName: string | null } | null;
  directReports: { roleLabel: string; assigneeName: string | null }[];
}

interface RoleDetailDataParams {
  /** Project GUID to fetch from. Null = no fetch. */
  projectGuid: string | null;
  /** Role slot GUID to filter assignments. Null = no fetch. */
  roleSlotGuid: string | null;
  /** Role label for the joined tasks. */
  roleLabel: string;
  /** Project name for the joined tasks. */
  projectName: string;
  /** User display-name lookup (guid→name) for hierarchy + assignee resolution. */
  userDisplayNames?: Map<string, string>;
  /**
   * DEV-1102 Phase 3 — optional refresh trigger. Bumping this value
   * forces a re-fetch of the underlying gantt + role-assignment data
   * without changing any of the identity inputs above. Used by the
   * EngagementsPage RACI Summary section after an inline Mark Done to
   * pick up the new percentComplete. Existing consumers
   * (ActiveRolesPanel, ProjectTeamPanel) don't pass this and behave
   * exactly as before.
   */
  refreshNonce?: number;
}

interface RoleDetailDataResult {
  tasks: EngagementTask[];
  allProjectGanttTasks: GanttTaskResponse[];
  /** All task-role assignments for the project, indexed by ganttTaskId (uppercase). */
  allTaskRoleAssignments: TaskRoleAssignmentMap;
  hierarchy: RoleDetailHierarchy | null;
  userDisplayNames: Map<string, string>;
  loading: boolean;
}

/* ── Hook ───────────────────────────────────────────────── */

export function useRoleDetailData(params: RoleDetailDataParams): RoleDetailDataResult {
  const { projectGuid, roleSlotGuid, roleLabel, projectName, userDisplayNames: externalNames, refreshNonce } = params;

  const [tasks, setTasks] = useState<EngagementTask[]>([]);
  const [allGantt, setAllGantt] = useState<GanttTaskResponse[]>([]);
  const [allTaskRoles, setAllTaskRoles] = useState<TaskRoleAssignmentMap>(new Map());
  const [hierarchy, setHierarchy] = useState<RoleDetailHierarchy | null>(null);
  const [loading, setLoading] = useState(false);

  // Stable fallback map to avoid creating a new object every render
  const emptyMap = useMemo(() => new Map<string, string>(), []);
  const names = externalNames ?? emptyMap;

  useEffect(() => {
    if (!projectGuid || !roleSlotGuid) {
      setTasks([]);
      setAllGantt([]);
      setAllTaskRoles(new Map());
      setHierarchy(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const [assignments, ganttTasks, roleSlots, allProjectTaskRoles] = await Promise.all([
          fetchTaskRoleAssignmentsByRole(projectGuid, roleSlotGuid),
          fetchGanttTasksByProject(projectGuid),
          fetchProjectRoleSlots(projectGuid),
          fetchTaskRoleAssignments(projectGuid),
        ]);

        if (cancelled) return;

        // ── Build task list ──
        const taskMap = new Map<string, GanttTaskResponse>();
        for (const gt of ganttTasks) taskMap.set(gt.id, gt);

        const joined: EngagementTask[] = [];
        for (const a of assignments) {
          const gt = taskMap.get(a.ganttTaskId);
          if (!gt) continue;
          joined.push({
            ganttTaskId: a.ganttTaskId,
            taskName: gt.taskName,
            startDate: gt.startDate,
            endDate: gt.endDate,
            percentComplete: gt.percentComplete,
            isMilestone: gt.isMilestone,
            responsibilityType: a.responsibilityType,
            roleSlotGuid: a.roleSlotGuid,
            roleLabel,
            projectGuid,
            projectName,
            predecessors: gt.predecessors ?? '',
            notes: gt.notes ?? '',
          });
        }
        setTasks(joined);
        setAllGantt(ganttTasks);
        setAllTaskRoles(buildTaskRoleAssignmentMap(allProjectTaskRoles));

        // ── Resolve reporting hierarchy ──
        const slotMap = new Map<string, ProjectRoleSlotResponse>();
        for (const s of roleSlots) {
          slotMap.set(s.roleSlotGuid.toUpperCase(), s);
        }

        const thisSlot = slotMap.get(roleSlotGuid.toUpperCase());
        let reportsTo: RoleDetailHierarchy['reportsTo'] = null;

        if (thisSlot?.reportsToSlotGuid) {
          const parentSlot = slotMap.get(thisSlot.reportsToSlotGuid.toUpperCase());
          if (parentSlot) {
            const parentAssignee = parentSlot.assignments?.find(
              (a) => a.status === 'active' || a.status === 'pending_verification'
            );
            const parentUser = parentAssignee
              ? names.get(parentAssignee.userGuid.toUpperCase()) ?? null
              : null;
            reportsTo = {
              roleLabel: parentSlot.roleLabel,
              assigneeName: parentUser,
            };
          }
        }

        const directReports: RoleDetailHierarchy['directReports'] = [];
        for (const [, slot] of slotMap) {
          if (
            slot.reportsToSlotGuid &&
            slot.reportsToSlotGuid.toUpperCase() === roleSlotGuid.toUpperCase()
          ) {
            const assignee = slot.assignments?.find(
              (a) => a.status === 'active' || a.status === 'pending_verification'
            );
            const assigneeName = assignee
              ? names.get(assignee.userGuid.toUpperCase()) ?? null
              : null;
            directReports.push({
              roleLabel: slot.roleLabel,
              assigneeName,
            });
          }
        }

        setHierarchy({ reportsTo, directReports });
      } catch (err) {
        console.error('[useRoleDetailData] Fetch failed:', err);
        if (!cancelled) {
          setTasks([]);
          setAllGantt([]);
          setAllTaskRoles(new Map());
          setHierarchy(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // DEV-1102 Phase 3 — refreshNonce intentionally in deps so callers
    // can force a re-fetch (e.g., after an inline Mark Done) without
    // changing identity. Undefined behaves like a constant (no extra
    // refetch) for existing consumers.
  }, [projectGuid, roleSlotGuid, roleLabel, projectName, names, refreshNonce]);

  return { tasks, allProjectGanttTasks: allGantt, allTaskRoleAssignments: allTaskRoles, hierarchy, userDisplayNames: names, loading };
}
